import React, {useEffect, useMemo, useState} from "react";
import {Spin} from "antd";
import {useStore} from "../../../hooks/useStore";
import {SelectField} from "./GeoLocateSelect.style";
import debounce from "lodash/debounce";
import {arrayAlphabeticalSort} from "../../../utils/helpers";
import {useFetch} from "../../../hooks/useFetch";

export const GeoLocateSelect = ({value, onChange, selectMarginBottom = "20px", receiverCity}) => {
    const stores = useStore();
    const [searchText, setSearchText] = useState();

    const addressValue = value?.address || value;

    const [loadingAddresses, addresses] = useFetch({
        fetchFn: stores.locationStore.listAutocompleteAddresses.bind(null, {
            address: receiverCity ? `${searchText || ""} ${receiverCity}` : searchText,
            components: "country:tr"
        }),
        defaultDataValue: [],
        dependencies: [searchText, receiverCity],
        initialCall: false,
        condition: searchText && searchText.trim().length >= 3
    });

    const debounceTimeout = 2000;
    const onSearch = useMemo(
        value => {
            const loadOptions = value => {
                const trimmed = (value || "").trim();
                if (trimmed?.length >= 3) {
                    setSearchText(value);
                } else {
                    setSearchText(undefined);
                }
            };
            return debounce(loadOptions, debounceTimeout);
        },
        [debounceTimeout]
    );

    const handleChange = async (value, option) => {
        if (!value) {
            onChange({
                address: "",
                location: undefined
            });
            return;
        }

        const locationDetail = await stores.locationStore.getLocationDetail({placeId: value});
        onChange({
            address: option.children,
            location: {
                longitude: locationDetail.coordinates.lng,
                latitude: locationDetail.coordinates.lat
            }
        });
    };

    const renderOptions = () => {
        if (!searchText || searchText.trim().length < 3) {
            return [];
        }

        const filteredAddresses = addresses.filter(address => {
            if (!receiverCity) return true;

            const addressParts = address.description.split(",").map(part => part.trim());
            const cityPart = addressParts[addressParts.length - 2]?.toLowerCase() || "";

            const normalizeStr = str => {
                return str
                    .toLowerCase()
                    .replace(/ı/g, "i")
                    .replace(/i̇/g, "i")
                    .replace(/ğ/g, "g")
                    .replace(/ü/g, "u")
                    .replace(/ş/g, "s")
                    .replace(/ö/g, "o")
                    .replace(/ç/g, "c");
            };

            const normalizedCity = normalizeStr(receiverCity);
            const normalizedCityPart = normalizeStr(cityPart);

            return normalizedCityPart.includes(normalizedCity);
        });

        return arrayAlphabeticalSort([...filteredAddresses], "description").map(
            (address, index) => (
                <SelectField.Option
                    key={index}
                    value={address?.place_id}
                    data-testid="geolocate-option"
                >
                    {address?.description}
                </SelectField.Option>
            )
        );
    };

    return (
        <React.Fragment>
            <SelectField
                showSearch
                value={addressValue}
                data-testid="geolocate-select"
                onChange={handleChange}
                mb={selectMarginBottom}
                allowClear
                loading={loadingAddresses}
                notFoundContent={loadingAddresses ? <Spin size="small" /> : null}
                onSearch={onSearch}
                filterOption={false}
            >
                {renderOptions()}
            </SelectField>
        </React.Fragment>
    );
};
